import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import '../../CSS/Investir.css';
import '../../CSS/indicateurs.css';
import Carousel from "../composant_reutilisable/Carousel";
import LienUtile from "../composant_reutilisable/LienUtile";
import { useLanguageData } from '../../hooks/useLanguageData';
import SafeHTML from "../SafeHTML";

const Investir = () => {
    const { t } = useTranslation();

    // Configuration des slugs pour les indicateurs
    const indicateursSlugs = [
        "investisseur_investir_pib_reel",
        "investisseur_investir_pib_taux",
        "investisseur_investir_taux_croissance",
        "investisseur_investir_taux_croissance_2",
        "investisseur_investir_pib_deficit",
        "investisseur_investir_taux_inflation"
    ];

    // Hook pour les données principales d'investissement
    const {
        data: investResponse,
        loading: investLoading,
        error: investError
    } = useLanguageData('/api/v1/embassy/static-data?slug=investir_benin_chine');

    // Hook pour les données des documents d'investissement
    const {
        data: documentsResponse,
        loading: documentsLoading,
        error: documentsError
    } = useLanguageData('/api/v1/embassy/static-data?slug=donne_investissement');

    const {
        data: ressourceResponse,
        loading: ressourceLoading,
        error: ressourceError
    } = useLanguageData('/api/v1/embassy/static-data?slug=donne_ressource');

    // Hooks individuels pour chaque indicateur
    const pibReelData = useLanguageData('/api/v1/embassy/static-data?slug=investisseur_investir_pib_reel');
    const pibTauxData = useLanguageData('/api/v1/embassy/static-data?slug=investisseur_investir_pib_taux');
    const tauxCroissanceData = useLanguageData('/api/v1/embassy/static-data?slug=investisseur_investir_taux_croissance');
    const tauxCroissanceData2 = useLanguageData('/api/v1/embassy/static-data?slug=investisseur_investir_taux_croissance_2');
    const pibDeficitData = useLanguageData('/api/v1/embassy/static-data?slug=investisseur_investir_pib_deficit');
    const tauxInflationData = useLanguageData('/api/v1/embassy/static-data?slug=investisseur_investir_taux_inflation');

    // Regrouper tous les indicateurs
    const allIndicators = [
        { name: 'pibReel', data: pibReelData },
        { name: 'pibTaux', data: pibTauxData },
        { name: 'tauxCroissance', data: tauxCroissanceData },
        { name: 'tauxCroissance2', data: tauxCroissanceData2 },
        { name: 'pibDeficit', data: pibDeficitData },
        { name: 'tauxInflation', data: tauxInflationData }
    ];

    // États pour les données extraites
    const [investData, setInvestData] = useState(null);
    const [indicateursData, setIndicateursData] = useState([]);
    const [documentsData, setDocumentsData] = useState([]);
    const [ressourceData, setRessourceData] = useState([]);
    const [documentsContent, setDocumentsContent] = useState('');
    const [ressourceContent, setRessourceContent] = useState('');
    const [documentsTitle, setDocumentsTitle] = useState('');
    const [ressourceTitle, setRessourceTitle] = useState('');

    // Fonction pour retirer l'extension .pdf
    const removePdfExtension = (fileName) => {
        if (!fileName) return '';
        return fileName.replace(/\.pdf$/i, '');
    };

    // Traiter les données d'investissement
    useEffect(() => {
        if (investResponse?.data && investResponse.data.length > 0) {
            console.log('📊 Données API investissement:', investResponse);
            setInvestData(investResponse.data[0]);
        }
    }, [investResponse]);

    // Traiter les données des documents
    useEffect(() => {
        if (documentsResponse?.data && documentsResponse.data.length > 0) {
            console.log('📄 Données API documents:', documentsResponse);
            const docData = documentsResponse.data[0];

            // Récupérer le titre depuis le backoffice
            if (docData.name) {
                setDocumentsTitle(docData.name);
            }

            // Récupérer le contenu
            if (docData.content) {
                setDocumentsContent(docData.content);
            }

            // Récupérer la bibliographie (fichiers PDF) - NOUVEAU FORMAT
            if (docData.biblio && Array.isArray(docData.biblio)) {
                const formattedDocs = docData.biblio.map((item, index) => {
                    // Utiliser le champ 'name' et retirer l'extension .pdf
                    let title = item.name ? removePdfExtension(item.name) : `Document ${index + 1}`;
                    
                    return {
                        id: index + 1,
                        title: title,
                        url: item.url
                    };
                });
                setDocumentsData(formattedDocs);
            }
        }
    }, [documentsResponse]);

    useEffect(() => {
        if (ressourceResponse?.data && ressourceResponse.data.length > 0) {
            console.log('📄 Données API ressources:', ressourceResponse);
            const docData = ressourceResponse.data[0];

            // Récupérer le titre depuis le backoffice
            if (docData.name) {
                setRessourceTitle(docData.name);
            }

            // Récupérer le contenu
            if (docData.content) {
                setRessourceContent(docData.content);
            }

            // Récupérer la bibliographie (fichiers PDF) - NOUVEAU FORMAT
            if (docData.biblio && Array.isArray(docData.biblio)) {
                const formattedDocs = docData.biblio.map((item, index) => {
                    // Utiliser le champ 'name' et retirer l'extension .pdf
                    let title = item.name ? removePdfExtension(item.name) : `Document ${index + 1}`;
                    
                    return {
                        id: index + 1,
                        title: title,
                        url: item.url
                    };
                });
                setRessourceData(formattedDocs);
            }
        }
    }, [ressourceResponse]);

    // Traiter tous les indicateurs
    useEffect(() => {
        const formattedIndicators = [];

        allIndicators.forEach(({ name, data }) => {
            if (data.data?.data && data.data.data.length > 0) {
                const item = data.data.data[0];
                formattedIndicators.push({
                    slug: name,
                    name: item.name,
                    label: item.name,
                    image: item.image,
                    content: item.content,
                    loading: data.loading,
                    error: data.error
                });
            }
        });

        if (formattedIndicators.length > 0) {
            setIndicateursData(formattedIndicators);
        }
    }, [pibReelData.data, pibTauxData.data, tauxCroissanceData.data, pibDeficitData.data, tauxInflationData.data]);

    // Vérifier si tous les indicateurs sont chargés
    const allIndicatorsLoaded = allIndicators.every(item => !item.data.loading);
    const indicateursLoading = !allIndicatorsLoaded;

    const loading = investLoading || indicateursLoading || documentsLoading || ressourceLoading;

    // Composant pour Information indisponible
    const InfoIndisponible = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic',
            fontSize: '1rem',
            textAlign: 'center',
            padding: '2rem',
            margin: '1rem 0'
        }}>
            {t('common.infoUnavailable')}
        </div>
    );

    // ============================================
    // COMPOSANT SECTION DOCUMENTS PDF
    // ============================================
    const SectionDocuments = () => {
        return (
            <div className="documents-section">
                <div className="documents-header">
                    {/* Utilisation du titre dynamique ou de la traduction par défaut */}
                    <h2 className="documents-title">
                        {documentsTitle || t('investmentData.title')}
                    </h2>
                    {documentsContent && (
                        <p className="documents-subtitle">
                            <SafeHTML html={documentsContent} />
                        </p>
                    )}
                </div>

                {documentsLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                    </div>
                ) : documentsError ? (
                    <InfoIndisponible />
                ) : documentsData.length > 0 ? (
                    <div className="documents-grid">
                        {documentsData.map((doc) => (
                            <div className="document-card" key={doc.id}>
                                <div className="document-content">
                                    <h3 className="document-title">{doc.title}</h3>
                                    <a
                                        href={doc.url}
                                        className="document-download-btn"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ borderColor: "#1a3c5e", color: "#1a3c5e" }}
                                    >
                                        <span>Consulter le PDF</span>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <InfoIndisponible />
                )}
            </div>
        );
    };

    const SectionRessource = () => {
        return (
            <div className="documents-section">
                <div className="documents-header">
                    {/* Utilisation du titre dynamique ou de la traduction par défaut */}
                    <h2 className="documents-title">
                        {ressourceTitle || t('investmentData.title')}
                    </h2>
                    {ressourceContent && (
                        <p className="documents-subtitle">
                            <SafeHTML html={ressourceContent} />
                        </p>
                    )}
                </div>

                {ressourceLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                    </div>
                ) : ressourceError ? (
                    <InfoIndisponible />
                ) : ressourceData.length > 0 ? (
                    <div className="documents-grid">
                        {ressourceData.map((doc) => (
                            <div className="document-card" key={doc.id}>
                                <div className="document-content">
                                    <h3 className="document-title">{doc.title}</h3>
                                    <a
                                        href={doc.url}
                                        className="document-download-btn"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ borderColor: "#1a3c5e", color: "#1a3c5e" }}
                                    >
                                        <span>Consulter le PDF</span>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <InfoIndisponible />
                )}
            </div>
        );
    };

    return (
        <div className="visite-container mt-4">
            <div className="visite-content">
                {/* Section principale Investir avec scroll */}
                {investLoading ? (
                    <div className="loading-container">
                        <div className="loading-spinner mt-4"></div>
                    </div>
                ) : investError || !investData ? (
                    <InfoIndisponible />
                ) : (
                    <div className="visite-texte">
                        <div className="visite-paragraphe">
                            <div className="content-visite" style={{
                                maxHeight: '600px',
                                overflowY: 'auto',
                                paddingRight: '15px'
                            }}>
                                <SafeHTML html={investData.content} />
                            </div>
                        </div>

                        {investData.image && (
                            <div className="visite-image-container">
                                <img
                                    src={investData.image}
                                    alt={t('invest.alt')}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Section Indicateurs clés */}
                <div className="indicateurs-container">
                    <h2 className="indicateurs-title">{t('invest.keyIndicators')}</h2>

                    {indicateursLoading ? (
                        <div className="indicateurs-loading">
                            <div className="loading-spinner"></div>
                        </div>
                    ) : indicateursData.length > 0 ? (
                        <div className="indicateurs-list">
                            {indicateursData.map((item, index) => (
                                <div className="indicateur-item" key={index}>
                                    <div className="indicateur-icon">
                                        {item.image ? (
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        ) : (
                                            <div className="indicateur-icon-placeholder">
                                                📊
                                            </div>
                                        )}
                                    </div>
                                    <div className="indicateur-info">
                                        <p className="indicateur-label">{item.label}</p>
                                        <div className="indicateur-value">
                                            <SafeHTML html={item.content} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <InfoIndisponible />
                    )}
                </div>

                {/* Section Documents PDF */}
                <SectionDocuments />

                {/* Section ressources investisseur Documents PDF */}
                <SectionRessource />

                {/* Lien Utile */}
                <LienUtile />
            </div>

            {/* Styles pour le chargement et le scroll */}
            <style>
                {`
                    .loading-container, .indicateurs-loading {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 2rem;
                        text-align: center;
                        min-height: 200px;
                        width: 100%;
                    }

                    .loading-spinner {
                        width: 40px;
                        height: 40px;
                        border: 4px solid #f3f3f3;
                        border-top: 4px solid #1a3c5e;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin-bottom: 1rem;
                    }

                    .indicateur-icon-placeholder {
                        font-size: 2rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 60px;
                        height: 60px;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    .indicateur-value h2 {
                        margin: 0;
                        padding: 0;
                    }

                    .indicateur-value .text-huge {
                        font-size: 1.5rem;
                        font-weight: bold;
                        color: #1a3c5e;
                    }

                    .indicateur-value .text-tiny {
                        font-size: 0.8rem;
                    }

                    /* Styles pour la barre de scroll */
                    .content-visite::-webkit-scrollbar {
                        width: 8px;
                    }

                    .content-visite::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 4px;
                    }

                    .content-visite::-webkit-scrollbar-thumb {
                        background: #c1c1c1;
                        border-radius: 4px;
                    }

                    .content-visite::-webkit-scrollbar-thumb:hover {
                        background: #a8a8a8;
                    }
                `}
            </style>
        </div>
    );
};

export default Investir;